import { useQuery } from '@tanstack/react-query';
import type { LeadResultsResponse } from '../types/leads';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface HomeStats {
  totalLeads: number;
  tierCounts: Record<string, number>;
  marketCounts: Record<string, number>;
  licenseCounts: Record<string, number>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * React Query hook to fetch lead summary stats for the home page.
 *
 * Fetches from /api/leads with include_counts=true and page_size=1
 * (we only need counts, not full results).
 *
 * Converts filter_counts arrays into Record objects for easier consumption.
 */
export function useHomeStats(): HomeStats {
  const { data, isLoading, error } = useQuery<LeadResultsResponse>({
    queryKey: ['home-stats'],
    queryFn: async () => {
      const response = await fetch(
        `${API_BASE}/api/leads?include_counts=true&page_size=1`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch home stats');
      }
      return response.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Convert filter_counts arrays to Record objects
  const tierCounts: Record<string, number> = {};
  const marketCounts: Record<string, number> = {};
  const licenseCounts: Record<string, number> = {};

  if (data?.filter_counts) {
    data.filter_counts.tier_counts.forEach((item) => {
      tierCounts[item.value] = item.count;
    });
    data.filter_counts.market_counts.forEach((item) => {
      marketCounts[item.value] = item.count;
    });
    data.filter_counts.license_counts.forEach((item) => {
      licenseCounts[item.value] = item.count;
    });
  }

  return {
    totalLeads: data?.total ?? 0,
    tierCounts,
    marketCounts,
    licenseCounts,
    isLoading,
    error: error as Error | null,
  };
}
